import VIP_GUIDE_EXTENSION from '@src/js/common/ark-extensions/dc-components/vip-guide/component-config';
import { createNanoEvents, DefaultEvents, Emitter } from 'nanoevents';
import ArkLoader from '@src/js/common/ark-extensions';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import i18n from 'i18next';
import { tdwWithVip } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import ua from '@tencent/docs-user-agent';
import DesktopModal from '@tencent/dui/lib/components/Modal';
import MobileModal from '@tencent/dui-mobile/lib/components/Modal';
import { PPTEditorStateEnumType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { RightSidebarModuleName } from '@src/slide/application/service/right-side-bar-service/core/constants';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { PersonalizeAnonymousStatus } from '@tencent/ark-domain/lib/collaborates/aggregate/collaborate-aggregate.interface';
import SnackbarPc from '@tencent/dui/lib/components/Snackbar';
import SnackbarM from '@tencent/dui-mobile/lib/components/Snackbar';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IRightSideBarService } from '@src/slide/application/service/right-side-bar-service/core/interface';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';

export const Modal = ua.isMobile ? MobileModal : DesktopModal;
const snackbar = ua.isMobile ? SnackbarM : SnackbarPc;
/**
 * 隐身设置状态
 * 0: 从未设置
 * 1: 开启
 * 2: 关闭
 */
export enum AnonymousStatus {
    NONE = PersonalizeAnonymousStatus.NONE,
    OPEN = PersonalizeAnonymousStatus.OPEN,
    CLOSED = PersonalizeAnonymousStatus.CLOSED,
    EXPIRED = 3,
}

export interface IServices {
    userPrivilegeService: IUserPriviledgeService;
    rightSidebarService: IRightSideBarService;
}

export interface IAnonymousViewService {
    anonymousStatus: boolean;
    isExpired: boolean;
    events: Emitter<DefaultEvents>;
    listenStatusChange(): Promise<void>;
    getAnonymousViewStatus(): Promise<boolean>;
    setAnonymousViewStatus(): Promise<void>;
    showAnonymousEditModal(onOk?: () => void, onCancel?: () => void): void;
    setServiceDelegates(services: IServices): void;
}

export class AnonymousViewService implements IAnonymousViewService {
    public anonymousStatus: boolean;
    public isExpired = false;
    public events = createNanoEvents();
    private modalInstance: any;
    private userPrivilegeService: IUserPriviledgeService;
    private rightSidebarService: IRightSideBarService;

    public setServiceDelegates(services: IServices): void {
        this.userPrivilegeService = services.userPrivilegeService;
        this.rightSidebarService = services.rightSidebarService;

        if (ua.isPC) {
            // 注册隐身状态切换监听，关闭版本及编辑记录右侧侧边栏
            [this.toggleHistoryEntryStatus, this.hideRightSidebar].forEach((callback) => {
                this.userPrivilegeService.onPPTEditorStateChange(callback);
            });
        }

        this.userPrivilegeService.addStateWillChangeCallbacks((state) => {
            if (state === PPTEditorStateEnumType.EDITABLE) {
                // 切为编辑态时，若已隐身，则不同意设为编辑态，若非隐身，则同意
                return !this.anonymousStatus;
            }
            // 切为非编辑态，无论是否隐身，都同意
            return true;
        });
    }

    public async listenStatusChange() {
        const sdkEventManager = await SlideDataServices.SDKDataServices.EventManager.promise();
        const cursorManager = await OnlineEditServices.CursorManager.promise();
        sdkEventManager.socketEvents.onAnonymousChange(({ status, operator }) => {
            const clientVars = SlideDataServices.ClientVarsService()?.getClientVars?.();
            const uid = clientVars?.userInfo?.uid || '';
            cursorManager.userManager.updateUserInfoCache();
            if (operator !== uid) return;
            this.anonymousStatus = status === AnonymousStatus.OPEN;
            this.isExpired = status === AnonymousStatus.EXPIRED;

            this.setPPTEditorState();

            this.events.emit('ANONYMOUS_CHANGE');
        });
    }

    /**
     * 获取隐身查看状态
     * @returns Promise<boolean>
     */
    public async getAnonymousViewStatus(): Promise<boolean> {
        try {
            const status = await this.getRealAnonymousViewStatus();
            if (status === AnonymousStatus.EXPIRED) {
                this.isExpired = true;
            } else {
                this.isExpired = false;
            }
            const anonymousStatus = status === AnonymousStatus.OPEN;
            this.anonymousStatus = anonymousStatus;
            this.setPPTEditorState();
            return this.anonymousStatus;
        } catch (error) {
            return this.anonymousStatus;
        }
    }

    /**
     * 设置隐身查看状态（提供给外部调用，需要判断 VIP 信息）
     * @returns
     */
    public async setAnonymousViewStatus() {
        try {
            const currentStatus = await this.getAnonymousViewStatus();

            // 判断会员不是开启状态, 则开启会员开通入口
            await ArkLoader.extensionService.scanAndHandleExtensions([VIP_GUIDE_EXTENSION]);
            const VipGuide = await ArkLoader.extensionService.getExtension(VIP_GUIDE_EXTENSION.name);
            VipGuide.show({
                guideType: 'setPersonalizeAnonymous',
                // NONE: 出现开通 VIP 提醒引导 OPEN: 设置匿名 CLOSED: 取消匿名
                anonymousStatus: currentStatus ? AnonymousStatus.OPEN : AnonymousStatus.CLOSED,
                position: 'titlebar',
                onError(error: any) {
                    console.error(error);
                    this.getAnonymousViewStatus();
                },
            });
        } catch (error) {
            global.log?.error(error);
            this.getAnonymousViewStatus();
        }
    }

    public showAnonymousEditModal(onOk?: () => void, onCancel?: () => void) {
        if (this.modalInstance || !this.anonymousStatus) {
            return;
        }
        this.modalInstance = Modal.confirm({
            title: i18n.t('提示'),
            destroyOnClose: true,
            content: i18n.t('编辑内容时，将取消隐身查看设置，点击确定取消隐身。'),
            style: {
                maxWidth: 450,
            },
            maskStyle: {
                zIndex: 10000,
            },
            onOk: () => {
                this.handleConfirm();
                tdwWithVip({
                    module: 'close',
                    opername: 'doc_vip_invisible',
                    action: 'click',
                    ver9: ua.isMobile ? 'mobile_editpopup' : 'pc_editsnackbar',
                });
                this.modalInstance = null;
                onOk?.();
            },
            onClose: () => {
                this.modalInstance = null;
                onCancel?.();
            },
            onCancel: () => {
                this.modalInstance = null;
                onCancel?.();
            },
        });
        tdwWithVip({
            module: 'close',
            opername: 'doc_vip_invisible',
            action: 'show',
            ver9: ua.isMobile ? 'mobile_editpopup' : 'pc_editsnackbar',
        });
    }

    /**
     * 设置隐身查看状态
     * @param status 隐身查看的状态
     * @returns boolean 是否设置成功
     */
    private async setRealAnonymousViewStatus(status: PersonalizeAnonymousStatus): Promise<boolean> {
        const fileId = SlideDataServices.DocInfoManager()?.padId ?? '';
        const { resData } = await ArkLoader.extensionService.dependentServiceCombinder.collaborateDomain
            .collaborateAggregate.setPersonalizeAnonymous(status, fileId);
        // resData.data有值说明设置成功，否则设置失败
        return !!resData?.data;
    }

    private async getRealAnonymousViewStatus(): Promise<number | undefined> {
        const fileId = SlideDataServices.DocInfoManager()?.padId ?? '';
        const { resData } = await ArkLoader.extensionService.dependentServiceCombinder.collaborateDomain
            .collaborateAggregate.getPersonalizeAnonymous(fileId);

        return resData?.data.result?.status || 0;
    }

    private setPPTEditorState() {
        const state = this.anonymousStatus ? PPTEditorStateEnumType.COPYABLE : this.userPrivilegeService.fileAuth;
        this.userPrivilegeService.setPPTEditorState({ state });
    }

    // 确认取消隐身查看
    private async handleConfirm(): Promise<void> {
        try {
            const isSucceed = await this.setRealAnonymousViewStatus(PersonalizeAnonymousStatus.CLOSED);
            if (isSucceed) {
                this.anonymousStatus = false;
                this.setPPTEditorState();
            } else {
                this.showFailTips();
            }
        } catch (error) {
            global.log?.error(error);
            this.showFailTips();
        }
    }

    // 取消隐身查看失败tips
    private showFailTips() {
        snackbar.show({
            message: i18n.t('取消隐身查看失败'),
            type: 'info',
            duration: 3000,
        });
    }

    /**
     * 关闭右侧侧边栏-版本及编辑记录（不关闭通知、快捷键、浏览记录）
     */
    private hideRightSidebar = (state: PPTEditorStateEnumType): void => {
        if (state === PPTEditorStateEnumType.EDITABLE) return;
        if (!this.anonymousStatus) return;
        const nameList = [
            RightSidebarModuleName.NOTIFY,
            RightSidebarModuleName.SHORTCUT,
            RightSidebarModuleName.READ_RECORD,
        ];
        if (nameList.some(name => this.rightSidebarService.isShown(name))) {
            return;
        }
        const rightSideBarService = this.rightSidebarService;
        if (!rightSideBarService) return;
        const visibleModule = rightSideBarService.getVisibleModule();
        if (visibleModule?.name === RightSidebarModuleName.HISTORY) {
            const module = visibleModule as unknown as { historyManager: IHistoryManager };
            module.historyManager.canUpdateEditorState = false;
        }
        this.rightSidebarService.hide();
    };

    /**
     * 控制版本及编辑记录入口显隐
     * @param pptEditorState PPTEditorStateEnumType
     * 编辑态展示入口
     * 非编辑态屏蔽入口
     */
    private toggleHistoryEntryStatus = async (pptEditorState: PPTEditorStateEnumType): Promise<void> => {
        await ArkLoader.initPromise;
        const component: any = await ArkLoader.extensionService.getExtension('docs-component-save-tips');
        component.store.dispatch.saveTips.setShouldShowSaveTips(pptEditorState === PPTEditorStateEnumType.EDITABLE);
    };
}
